import { useEffect, useState } from "react";
import { getMyProfile, requestAgentAccess, getWalletTransactions } from "../../api/client";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import ApplicationStatusCard from "../../components/ApplicationStatusCard";
import LoadingIndicator from "../../components/LoadingIndicator";

export default function AgentDashboard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);

  const [agentForm, setAgentForm] = useState({
    locationType: "physical",
    physicalAddress: "",
    nin: "",
    guarantorName: "",
    guarantorPhone: "",
    guarantorAddress: ""
  });
  const [agentBusy, setAgentBusy] = useState(false);
  const [agentMessage, setAgentMessage] = useState("");
  const [agentError, setAgentError] = useState("");

  useEffect(() => {
    getMyProfile()
      .then(r => setProfile(r.data))
      .catch(() => setAgentError("Could not load your profile."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (profile?.agentApprovalStatus !== "approved") return;
    getWalletTransactions()
      .then(r => setCommissions((r.data || []).filter(tx => tx.type === "agent_commission")))
      .catch(() => {});
  }, [profile?.agentApprovalStatus]);

  const handleAgentSubmit = async (e) => {
    e.preventDefault();
    setAgentError("");
    setAgentMessage("");
    setAgentBusy(true);
    try {
      const { data } = await requestAgentAccess({
        locationType: agentForm.locationType,
        physicalAddress: agentForm.locationType === "physical" ? agentForm.physicalAddress : "",
        nin: agentForm.nin,
        guarantor: { name: agentForm.guarantorName, phone: agentForm.guarantorPhone, address: agentForm.guarantorAddress }
      });
      setAgentMessage("✓ Agent access request submitted — an admin will review and approve your account.");
      setProfile(p => ({ ...p, agentApprovalStatus: data.agentApprovalStatus }));
    } catch (err) {
      setAgentError(err.response?.data?.error || "Could not submit Agent access request.");
    } finally {
      setAgentBusy(false);
    }
  };

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader backTo="/business" backLabel="Businesses" />

        <div className="page">
          <div className="brand-header">
            <span className="brand-logo">🧑‍💼</span>
            <span className="brand-name">Agent</span>
            <p className="brand-tagline">Book deliveries on behalf of walk-in customers</p>
          </div>

          {loading ? (
            <LoadingIndicator />
          ) : profile?.agentApprovalStatus === "pending" ? (
            <ApplicationStatusCard
              status="pending"
              title="Application Under Review"
              description="Your Agent access request is being reviewed by our admin team. You'll have access once approved."
            />
          ) : profile?.agentApprovalStatus === "revoked" ? (
            <ApplicationStatusCard
              status="revoked"
              title="Agent Access Revoked"
              description="Your Agent access was revoked by an administrator. Please contact support for more information."
            />
          ) : (
            <div className="delivery-card">
              {profile?.agentApprovalStatus === "approved" ? (
                <>
                  <p className="hint" style={{ marginBottom: "8px" }}>✓ Your account is an approved Agent.</p>
                  <p className="hint">
                    Every delivery you book carries a small service fee already built into the price shown —
                    there's nothing extra to add at checkout.
                  </p>

                  <h3 style={{ marginTop: "28px" }}>Commission History</h3>
                  {commissions.length === 0 ? (
                    <div className="empty-state">
                      <span className="empty-state-icon">📋</span>
                      <p className="empty-state-text">No commissions yet — they'll show up here once you book deliveries.</p>
                    </div>
                  ) : (
                    commissions.map((tx, i) => (
                      <div key={tx._id || i} className="history-row">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <strong>Order #{tx.order?._id ? tx.order._id.slice(-6) : "—"}</strong>
                            <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                              {tx.order?.status ? `${tx.order.status} · ` : ""}
                              {new Date(tx.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <strong style={{ fontSize: "16px", color: "var(--text-accent)" }}>
                            +₦{tx.amount.toLocaleString()}
                          </strong>
                        </div>
                      </div>
                    ))
                  )}
                </>
              ) : (
                <>
                  <p className="hint" style={{ marginBottom: "12px" }}>
                    Book deliveries on behalf of walk-in customers — a corner shop taking in packages, for example.
                    Requires ID verification.
                  </p>
                  <form onSubmit={handleAgentSubmit}>
                    <div className="form-field">
                      <label>Location Type</label>
                      <select value={agentForm.locationType} onChange={e => setAgentForm(f => ({ ...f, locationType: e.target.value }))}>
                        <option value="physical">Physical Location</option>
                        <option value="remote">Remote</option>
                      </select>
                    </div>
                    {agentForm.locationType === "physical" && (
                      <div className="form-field">
                        <label>Physical Address</label>
                        <input type="text" value={agentForm.physicalAddress} onChange={e => setAgentForm(f => ({ ...f, physicalAddress: e.target.value }))} required />
                      </div>
                    )}
                    <div className="form-field">
                      <label>National Identification Number (NIN)</label>
                      <input type="text" value={agentForm.nin} onChange={e => setAgentForm(f => ({ ...f, nin: e.target.value }))} required />
                    </div>
                    <div className="form-field">
                      <label>Guarantor's Full Name</label>
                      <input type="text" value={agentForm.guarantorName} onChange={e => setAgentForm(f => ({ ...f, guarantorName: e.target.value }))} required />
                    </div>
                    <div className="form-field">
                      <label>Guarantor's Phone Number</label>
                      <input type="tel" value={agentForm.guarantorPhone} onChange={e => setAgentForm(f => ({ ...f, guarantorPhone: e.target.value }))} required />
                    </div>
                    <div className="form-field">
                      <label>Guarantor's Address</label>
                      <input type="text" value={agentForm.guarantorAddress} onChange={e => setAgentForm(f => ({ ...f, guarantorAddress: e.target.value }))} required />
                    </div>
                    {agentError && <p className="error">{agentError}</p>}
                    {agentMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{agentMessage}</p>}
                    <button type="submit" disabled={agentBusy}>{agentBusy ? "Submitting..." : "Request Agent Access"}</button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
